import { useEffect, useState } from "react";
import { AtsPaginationParams, AtsPaginationResults } from "src/network/company_ats/type";

import { customToast } from "src/utils/toast";

interface IUsePagination<T, FILTER> {
  fetchPage: (params: AtsPaginationParams & any) => Promise<AtsPaginationResults<T>>;
  page_size: number;
  filters?: FILTER;
}

/**
 * This Hook can be used for handling most of the pagination fetching data from merge ats which uses cursors
 */
function useAtsPagination<T, FILTER = any>({
  fetchPage,
  page_size = 20,
  filters
}: IUsePagination<T, FILTER>) {
  const [data, setData] = useState<T[]>();
  const [cursor, setCursor] = useState<string | null>();
  const [fetching, setFetching] = useState<boolean>();

  const fetch = async () => {
    if (fetching) return;
    setFetching(true);
    try {
      const result = await fetchPage({
        cursor,
        offset: data?.length || 0,
        page_size,
        ...filters
      });
      setCursor(result.cursor || null);
      setData([...(data || []), ...result.data]);
    } catch (e) {
      console.error(e);
      customToast({ message: "Error fetching page" });
    }
    setFetching(false);
  };

  // Refresh (if filters change, fetch changes too)
  useEffect(() => {
    setCursor(undefined);
    setData(undefined);
  }, [filters]);

  // No data is the signal to fetch
  useEffect(() => {
    if (!data) fetch();
  }, [data]);

  return {
    fetching,
    done: cursor === null,
    data,
    fetch,
    reset: () => setData(undefined),
    refresh: () => setData(data?.slice())
  };
}

export default useAtsPagination;
